import { Tabs } from "@msys/ui";
import CategoryIcon from "@mui/icons-material/Category";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useTranslate } from "@tolgee/react";
import { useMemo } from "react";
import {
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { RestrictedByOrganisationPermission } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { PageTopbarItem } from "../../commons/layout/Page.js";
import {
  ProductListItemOverviewModal,
  ProductListOverview,
} from "./ProductListOverview.js";
import { ProductLists } from "./ProductLists.js";
import { ProductOverview } from "./ProductOverview.js";
import { ProductsCatalogues } from "./ProductsCatalogues.js";
import { SupplierCataloguesBox } from "./SupplierCatalogues.js";
import { SupplierDiscountGroups } from "./SupplierDiscountGroups.js";
import { SupplierPimImportations } from "./SupplierPimImportations.js";
import { SupplierProductEdit } from "./SupplierProductEdit.js";
import { SupplierProfile } from "./SupplierProfile.js";
import { Suppliers } from "./Suppliers.js";

type PageSubmenuItem = PageTopbarItem;

export const ProductsRoutes = () => {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate(["OrganisationPageTopbar"]);

  const submenuItems = useMemo(() => {
    if (!viewer.organisation) return [];

    const items: PageSubmenuItem[] = [
      {
        name: "products-catalogues",
        label: t("Product Catalogues", { ns: "OrganisationPageTopbar" }),
        path: `/products/catalogues`,
        icon: <CategoryIcon />,
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "suppliers",
        label: t("Suppliers", { ns: "OrganisationPageTopbar" }),
        path: `/products/suppliers`,
        icon: <LocalShippingIcon />,
        permission: "pim_catalogue_update",
      },
      {
        name: "product-lists",
        label: t("Product lists", { ns: "OrganisationPageTopbar" }),
        path: `/products/lists`,
        icon: <ListAltIcon />,
        permission: "pim_catalogue_update",
      },
    ];
    return items.filter(item => !item.isHidden);
  }, [t, viewer.organisation]);

  const pathToProductListsList = "/products/lists";

  return (
    <Routes>
      <Route index element={<Navigate to="catalogues" replace />} />
      <Route path="catalogues">
        <Route
          index
          element={<ProductsCatalogues submenuItems={submenuItems} />}
        />
        <Route
          path="supplier/:supplierId/:articleNumber/overview"
          element={<ProductOverview submenuItems={[]} />}
        />
        <Route
          path="supplier/:supplierId/:articleNumber/edit"
          element={<SupplierProductEdit submenuItems={[]} />}
        />
      </Route>
      <Route path="suppliers">
        <Route
          index
          element={
            <RestrictedByOrganisationPermission permission="pim_catalogue_update">
              <Suppliers submenuItems={submenuItems} />
            </RestrictedByOrganisationPermission>
          }
        />
        <Route
          path=":supplierId"
          element={
            <RestrictedByOrganisationPermission permission="pim_catalogue_update">
              <SupplierProfile submenuItems={submenuItems}>
                <SupplierTabs />
                <Outlet />
              </SupplierProfile>
            </RestrictedByOrganisationPermission>
          }
        >
          <Route
            index
            element={<Navigate to="imported-catalogues" replace />}
          />
          <Route path="discount-groups" element={<SupplierDiscountGroups />} />
          <Route
            path="imported-catalogues"
            element={<SupplierPimImportations />}
          />
          <Route
            path="subscribed-catalogues"
            element={<SupplierCataloguesBox />}
          />
        </Route>
      </Route>

      <Route path="lists">
        <Route
          index
          element={
            <RestrictedByOrganisationPermission permission="pim_catalogue_update">
              <ProductLists
                submenuItems={submenuItems}
                pathToProductListsList={pathToProductListsList}
              />
            </RestrictedByOrganisationPermission>
          }
        />
        <Route
          path=":id"
          element={
            <RestrictedByOrganisationPermission permission="pim_catalogue_update">
              <ProductListOverview
                submenuItems={submenuItems}
                pathToProductListsList={pathToProductListsList}
              />
            </RestrictedByOrganisationPermission>
          }
        >
          <Route
            path="product/:supplierId/:articleNumber"
            element={
              <ProductListItemOverviewModal
                pathToProductListsList={pathToProductListsList}
              />
            }
          />
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

const SupplierTabs = () => {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const navigate = useNavigate();
  const location = useLocation();
  const path = useResolvedPath("");

  const { t } = useTranslate([
    "CrmOrganisations",
    "PimImportations",
    "SupplierCatalogue",
  ]);

  const tabItems = [
    {
      label: t("Imported catalogues", { ns: "PimImportations" }),
      value: "imported-catalogues",
    },
    {
      label: t("Subscribable catalogues", { ns: "SupplierCatalogue" }),
      value: "subscribed-catalogues",
    },
    {
      label: t("Discount groups", { ns: "CrmOrganisations" }),
      value: "discount-groups",
    },
  ];
  const activeTab = tabItems.find(item =>
    matchPath(`${path.pathname}/${item.value}`, location.pathname)
  );

  return (
    <Tabs
      options={tabItems}
      value={activeTab?.value}
      onChange={value => {
        const tab = tabItems.find(t => t.value === value);
        if (tab) navigate(`${path.pathname}/${tab.value}`);
      }}
    />
  );
};
